import { PlayCircleIcon } from '@langchain/untitled-ui-icons';
import Chip from '@mui/joy/Chip';

import { useNavigate } from 'react-router-dom';

import { Tooltip } from '@/design-system';
import { cn } from '@/utils/tailwind';

import { useRun } from '../hooks/useSwr';
import { RunSchema } from '../types/schema';

function PlaygroundRunTag(props: {
  parentRunId: string;
  sessionId?: string;
  startTime?: string;
}) {
  const run = useRun({
    id: props.parentRunId,
    excludeSerialized: true,
    sessionId: props.sessionId,
    startTime: props.startTime,
  });
  const navigate = useNavigate();

  if (run.data == null) return null;
  return (
    <Chip
      size={'sm'}
      color="neutral"
      variant={'outlined'}
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
      startDecorator={<PlayCircleIcon className="z-auto size-4" />}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();

        if (run.data?.app_path != null) {
          navigate(run.data?.app_path);
        }
      }}
    >
      <div className="z-auto leading-none">{run.data.name}</div>
    </Chip>
  );
}

function RunTag(props: {
  tag: string;
  isPlaygroundHidden: boolean;
  sessionId?: string;
  startTime?: string;
}) {
  if (props.tag.includes(':') && props.tag.split(':')[0] === 'playground') {
    if (props.tag.split(':')[1] === 'repo') return null; // TODO render something for playground:repo
    if (props.isPlaygroundHidden) return null;
    const parentRunId = props.tag.split(':')[1];
    return (
      <PlaygroundRunTag
        parentRunId={parentRunId}
        sessionId={props.sessionId}
        startTime={props.startTime}
      />
    );
  }

  return (
    <div className="whitespace-nowrap rounded-md border border-secondary px-1 py-0.5 text-xs">
      {props.tag}
    </div>
  );
}

const HIDDEN = ['langsmith:hidden'];

export function RunTags({
  run,
  hidden,
  isPublicPage = false,
  showMoreIndicator = true,
  noWrap = false, // Set to true to prevent wrapping (used in table contexts)
}: {
  run?: RunSchema | null;
  hidden?: string[];
  isPublicPage?: boolean;
  showMoreIndicator?: boolean;
  noWrap?: boolean;
}) {
  const tags = run?.tags?.filter?.(
    (t) => !hidden?.includes(t) && !HIDDEN.includes(t)
  );

  if (!tags?.length) return null;

  const maxTags = 1;

  // If showMoreIndicator is false, show all tags without limit
  if (!showMoreIndicator || tags.length <= maxTags) {
    return (
      <div className={`flex gap-1 ${noWrap ? 'flex-nowrap' : 'flex-wrap'}`}>
        {tags.map((t) => (
          <RunTag
            key={t}
            tag={t}
            isPlaygroundHidden={isPublicPage}
            sessionId={run?.session_id}
            startTime={run?.start_time}
          />
        ))}
      </div>
    );
  }

  // Show first maxTags tags and "+N more" with tooltip (only if showMoreIndicator is true)
  const visibleTags = tags.slice(0, maxTags);
  const remainingTags = tags.slice(maxTags);
  const remainingCount = remainingTags.length;

  const visibleChildren = visibleTags.map((t) => (
    <RunTag
      key={t}
      tag={t}
      isPlaygroundHidden={isPublicPage}
      sessionId={run?.session_id}
      startTime={run?.start_time}
    />
  ));

  const remainingTagsTooltip = (
    <div className="flex flex-col items-start gap-1">
      {remainingTags.map((t) => (
        <RunTag
          key={t}
          tag={t}
          isPlaygroundHidden={isPublicPage}
          sessionId={run?.session_id}
          startTime={run?.start_time}
        />
      ))}
    </div>
  );

  return (
    <div className={`flex gap-1 ${noWrap ? 'flex-nowrap' : 'flex-wrap'}`}>
      {visibleChildren}
      {showMoreIndicator && (
        <Tooltip title={remainingTagsTooltip} side="top">
          <div
            className={cn(
              'cursor-help whitespace-nowrap rounded-md border border-secondary px-1 py-0.5 text-xs'
            )}
          >
            +{remainingCount} more
          </div>
        </Tooltip>
      )}
    </div>
  );
}
